import Bugsnag from '@bugsnag/js'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Grid,
  Modal,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { BsPlusLg } from 'react-icons/bs'
import { MdExpandLess, MdExpandMore, MdNavigateBefore } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { DPCContract, Formtitle, InvoiceForm, Layout, Rac, StripeContract, StripeModal } from '../components'
import FundingSummary from '../components/FundingSummary'
import ProductSummary from '../components/ProductsSummary'
import { client } from '../utils/client'
import { hasKnownCvDates } from '../utils/metropolitanCv'
import { getListingPrice } from '../utils/pricing'
import { FormType, NormalFormData } from '../utils/sharedTypes'
import AlmaContract from '../components/AlmaContract'
import MetropolitanCvNote from '../components/MetropolitanCvNote'
import { StripeSession } from '../components/StripeModal'
import BankTransferModal from '../components/BankTransferModal'
import {
  getGcScheduleSummary as getGcScheduleSummaryBase,
  isBillingInfoComplete,
  isRecapSubmitDisabled,
  resolveAlmaPaymentAmount,
  shouldPay as shouldPayRegistration,
  toAlmaAmountInCents,
} from '../utils/registrationPayment'

type GoCardlessInstallment = {
  due_date: string
  amount: number
  provider: string
  status: string
}

type GoCardlessStatus = {
  contract_signed: boolean
  mandate_status: string | null
  mandate_ready: boolean
  card_registered: boolean
  installments: GoCardlessInstallment[]
}

const gcFormatter = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' })

const formatGcDate = (dueDate: string) => new Date(dueDate).toLocaleDateString('fr-FR')

type RecapPropsType = {
  form: FormType
  setForm: React.Dispatch<React.SetStateAction<FormType>>
}

declare const window: Window & {
  Alma: Alma
}

type Alma = {
  InPage: {
    initialize: (data: AlmaInitialize) => AlmaInPage
  }
}

type AlmaInitialize = {
  merchantId?: string
  amountInCents?: number
  installmentsCount?: number
  selector: string
  environment?: 'PROD' | 'TEST'
}

type AlmaInPage = {
  startPayment: (data: AlmaStartPayment) => void
  unmount: () => void
}

type AlmaStartPayment = {
  paymentId: string
  onPaymentSucceeded: () => void
  onPaymentRejected: () => void
  onUserCloseModal: () => void
}

const Recap = ({ form, setForm }: RecapPropsType) => {
  const [stripeSession, setStripeSession] = useState<StripeSession>()
  const [stripeRedirectUrl, setStripeRedirectUrl] = useState<string | undefined>(undefined)
  const [inPage, setInPage] = useState<AlmaInPage | undefined>(undefined)
  const [reload, setReload] = useState(false)
  const [loading, setLoading] = useState(false)
  const [openStripeModal, setOpenStripeModal] = useState(false)
  const [openBankTransferModal, setOpenBankTransferModal] = useState(false)
  const [gcStatus, setGcStatus] = useState<GoCardlessStatus | null>(null)
  const [gcStatusLoading, setGcStatusLoading] = useState(false)
  const [gcActionLoading, setGcActionLoading] = useState(false)
  const [gcError, setGcError] = useState<string | null>(null)
  const [gcScheduleExpanded, setGcScheduleExpanded] = useState(false)
  const [billingInfoComplete, setBillingInfoComplete] = useState(isBillingInfoComplete(form.data))
  const [paymentUrl, setPaymentUrl] = useState('')
  const [openFailedPaymentModal, setOpenFailedPaymentModal] = useState(false)
  const preferedPaymentProvider = form.data.preferred_payment_method || 'stripe'

  // Cf. src/utils/pricing.ts : ce montant (utilisé comme fallback pour l'initialisation
  // du paiement Alma ci-dessous) fait désormais foi sur la même formule que FundingSummary.tsx,
  // avec repli sur funding_organism_registrations[0] si product est null (au lieu de 0 auparavant).
  const listing_price = getListingPrice(form.data)

  const shouldPay = shouldPayRegistration(
    form.data.funding_organism,
    form.data.personal_funding,
    form.data.default_personal_funding,
  )

  const navigate = useNavigate()

  const gcScheduleSummary = useMemo(() => {
    const summary = getGcScheduleSummaryBase(gcStatus?.installments ?? [])
    return {
      ...summary,
      lastSepaDate: summary.lastSepaDate ? formatGcDate(summary.lastSepaDate) : null,
    }
  }, [gcStatus?.installments])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    if (form.data.funding_organism?.code !== 'GOCARDLESS' || !form.data.global_id) return
    setGcStatusLoading(true)
    client
      .get(`/registration/${form.data.global_id}/gocardless_status/`)
      .then((resp) => setGcStatus(resp.data as GoCardlessStatus))
      .catch((err: Error) => Bugsnag.notify(err))
      .finally(() => setGcStatusLoading(false))
  }, [form.data.funding_organism?.code, form.data.global_id])

  useEffect(() => {
    if (stripeSession) {
      setOpenStripeModal(true)
    } else {
      setOpenStripeModal(false)
    }
  }, [stripeSession])

  const session = form.sessions.find((session) => session.global_id === form.data.session?.global_id)
  const showMetropolitanCvNote = hasKnownCvDates({
    product: form.data.product,
    products: form.data.products,
    session: form.data.session,
    sessions: form.sessions,
  })

  const submitForm = async () => {
    setLoading(true)
    if (form.data.funding_organism?.code === 'GOCARDLESS') {
      if (form.data.validated || gcStatus?.card_registered) {
        setLoading(false)
        navigate({ pathname: './../recommandation', search: location.search })
        return
      }
      if (!gcStatus?.mandate_ready) {
        setLoading(false)
        navigate(
          { pathname: './../financement/', search: location.search },
          {
            state: {
              gcError:
                'Le mandat SEPA doit être confirmé avant l’enregistrement de la carte bancaire.',
            },
          },
        )
        return
      }
      await startGcCardPayment()
      setLoading(false)
      return
    }
    if (shouldPay) {
      if (preferedPaymentProvider == 'alma' || form.data.funding_organism?.code == 'ALMA') {
        if (form.data.validated) {
          // Prevent re-trigger Alma payment if the registration is validated
          navigate({ pathname: './../alma', search: `${location.search}&success` })
          return
        }
        client
          .post(`/registration/${form.data.global_id}/pay_alma/`, form.data)
          .then((resp) => {
            const data = resp.data as NormalFormData
            try {
              inPage?.startPayment({
                paymentId: data.alma_payment_id,
                onPaymentSucceeded: () => {
                  navigate({ pathname: './../alma', search: `${location.search}&success` })
                },
                onPaymentRejected: () => {
                  navigate({ pathname: './../alma', search: `${location.search}&cancel` })
                },
                onUserCloseModal: () => {
                  navigate({ pathname: './../alma', search: `${location.search}&cancel` })
                },
              })
            } catch (e) {
              window.open(data.alma_payment_url, '_self')
              setPaymentUrl(data.alma_payment_url)
              setOpenFailedPaymentModal(true)
            }
          })
          .catch((err: Error) => {
            Bugsnag.notify(err)
            console.error(err)
          })
          .finally(() => {
            setLoading(false)
          })
      } else if (form.data.funding_organism?.code == 'BANK_TRANSFER') {
        // Bank transfert sales
        if (form.data.validated) {
          // Prevent re-trigger Bank Transfer modal if the registration is validated
          navigate({ pathname: './../recommandation', search: location.search })
          return
        }
        client
          .patch(`/registration/${form.data.global_id}/submit/`, form.data)
          .then((resp) => {
            if (resp.status === 200) {
              navigate({ pathname: './../virement', search: location.search })
            }
          })
          .catch((err: Error) => {
            Bugsnag.notify(err)
            console.error(err)
          })
          .finally(() => {
            setLoading(false)
          })
      } else if (preferedPaymentProvider == 'bank_transfer') {
        // Special case for RAC paid by bank transfer
        if (form.data.validated) {
          // Prevent re-trigger Bank Transfer modal if the registration is validated
          navigate({ pathname: './../recommandation', search: location.search })
          return
        }
        client
          .patch(`/registration/${form.data.global_id}/submit/`, form.data)
          .then((resp) => {
            if (resp.status === 200) {
              setOpenBankTransferModal(true)
            }
          })
          .catch((err: Error) => {
            Bugsnag.notify(err)
            console.error(err)
          })
          .finally(() => {
            setLoading(false)
          })
      } else {
        // First submit the form to set the validated flag to true
        const resp = await client.patch(`/registration/${form.data.global_id}/submit/`, form.data)
        if (resp.status === 200) {
          if (form.data.validated) {
            // Prevent re-trigger Stripe checkout if the registration is validated
            navigate({ pathname: './../recommandation', search: location.search })
            return
          }
          client
            .post(`/registration/${form.data.global_id}/create_stripe_checkout_session/`, form.data)
            .then((resp: { data: StripeSession }) => {
              setStripeSession(resp.data)
            })
            .catch((err) => console.error(err))
            .finally(() => {
              setLoading(false)
            })
        } else {
          Bugsnag.notify(new Error('Failed to submit form'))
          console.error('Failed to submit form')
          setLoading(false)
        }
      }
    } else {
      client
        .patch(`/registration/${form.data.global_id}/submit/`, form.data)
        .then((resp) => {
          if (resp.status === 200) {
            navigate({ pathname: './../recommandation', search: location.search })
          }
        })
        .catch((err: Error) => {
          Bugsnag.notify(err)
          console.error(err)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  const startGcCardPayment = async () => {
    if (!form.data.global_id) return
    setGcActionLoading(true)
    setGcError(null)
    try {
      // Re-check mandate_ready before opening Stripe — webhook may have arrived after page load,
      // or the user may have skipped the financement gate.
      const statusResp = await client.get(`/registration/${form.data.global_id}/gocardless_status/`)
      const freshStatus = statusResp.data as GoCardlessStatus
      setGcStatus(freshStatus)
      if (!freshStatus.mandate_ready) {
        navigate(
          { pathname: './../financement/', search: location.search },
          {
            state: {
              gcError:
                'Le mandat SEPA doit être confirmé avant l’enregistrement de la carte bancaire.',
            },
          },
        )
        return
      }

      const resp = await client.post(`/registration/${form.data.global_id}/create_stripe_checkout_session/`)
      const data = resp.data as { clientSecret: string; type: 'payment' | 'setup'; amount: number }
      const stripePath = window.location.pathname.includes('/sante') ? '/sante/stripe' : '/stripe'
      setStripeRedirectUrl(`${window.location.origin}${stripePath}/?global_id=${form.data.global_id}`)
      setStripeSession({ clientSecret: data.clientSecret, type: data.type, amount: data.amount })
    } catch (err) {
      Bugsnag.notify(err as Error)
      setGcError('Une erreur est survenue lors de la préparation du paiement. Veuillez réessayer.')
    } finally {
      setGcActionLoading(false)
    }
  }

  const refetchRegistration = () => {
    client
      .get(`/registration/${form.data.global_id}/`)
      .then((resp) => {
        const data = resp.data as NormalFormData
        setForm((prevState) => {
          return {
            ...prevState,
            data: {
              ...prevState.data,
              ...data,
            },
          }
        })
        setReload(!reload)
      })
      .catch((err: Error) => {
        Bugsnag.notify(err)
        console.error(err)
      })
  }

  const isSubmitDisabled = () => {
    return isRecapSubmitDisabled({
      loading,
      fundingOrganism: form.data.funding_organism,
      preferredPaymentMethod: preferedPaymentProvider,
      shouldPay,
      billingInfoComplete,
      inPageReady: Boolean(inPage),
      validated: form.data.validated,
      stripeContractSignedAt: form.data.stripe_contract_signed_at,
      stripeCguCgv: form.data.stripe_cgu_cgv,
      persoProgram: form.data.perso_program,
      persoCguCgv: form.data.perso_cgu_cgv,
      persoSignature: form.data.perso_signature,
      persoCheckbox: form.data.perso_checkbox,
      dpcCguCgv: form.data.dpc_cgu_cgv,
      dpcContractSignedAt: form.data.dpc_contract_signed_at,
      opcoAmount: form.data.opco_amount,
      personalFunding: form.data.personal_funding,
      gcStatusLoading,
      gcActionLoading,
      gcMandateReady: Boolean(gcStatus?.mandate_ready),
      gcCardRegistered: Boolean(gcStatus?.card_registered),
    })
  }

  useEffect(() => {
    if (
      form.data.funding_organism?.code === 'ALMA' ||
      (form.data.personal_funding !== null && Boolean(form.data.personal_funding)) ||
      (form.data.personal_funding === null && Boolean(form.data.default_personal_funding))
    ) {
      const externalScript = document.createElement('script')
      externalScript.src = 'https://cdn.jsdelivr.net/npm/@alma/in-page@2.x/dist/index.umd.js'
      externalScript.async = true
      externalScript.onload = () => {
        const amount = resolveAlmaPaymentAmount(
          form?.data?.personal_funding,
          form?.data?.default_personal_funding,
          listing_price,
        )
        const inPage = window.Alma.InPage.initialize({
          amountInCents: toAlmaAmountInCents(amount),
          installmentsCount: form.data.alma_installments_count ?? 1,
          selector: '#alma-in-page',
          environment: import.meta.env.VITE_ENV === 'PRODUCTION' ? 'PROD' : 'TEST',
        })
        setInPage(inPage)
      }
      document.head.appendChild(externalScript)
    }
  }, [form.data.funding_organism?.code, form.data.personal_funding, form.data.default_personal_funding, reload])

  return (
    <Layout
      form={form.data}
      steps={
        form.data.funding_organism?.code === 'STRIPE' ? form.steps.filter((s) => s.url !== 'financement') : form.steps
      }
    >
      <Modal
        open={openFailedPaymentModal}
        sx={{
          p: { xs: 1, md: 20 },
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Grid
          container
          direction="column"
          justifyContent="center"
          alignItems="center"
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: {
              sm: '90%',
              md: '600px',
            },
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 5,
            borderRadius: '4px',
          }}
        >
          <Grid
            item
            xs={12}
            sx={{
              background: 'white',
              width: '100%',
              '&:focus': {
                outline: 'none',
              },
            }}
          >
            <Grid
              container
              item
              xs={12}
              sx={{
                position: 'relative',
                top: 0,
                justifyContent: 'end',
              }}
            >
              <Grid
                container
                justifyContent="center"
                alignItems="center"
                sx={{
                  width: 40,
                  height: 40,
                  position: 'absolute',
                  background: 'white',
                  borderRadius: '50%',

                  top: -15,
                  transform: 'rotate(45deg)',

                  cursor: 'pointer',
                  boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.25)',
                }}
                onClick={() => {
                  setOpenFailedPaymentModal(false)
                }}
              >
                <BsPlusLg />
              </Grid>
            </Grid>

            <Grid container direction="column" alignItems="center" item sx={{ maxHeight: '100%', overflow: 'scroll' }}>
              <Typography variant="h5" textAlign="center" fontWeight="600" mb={2}>
                Paiement
              </Typography>
              <Typography variant="body1" mb={2}>
                Vous allez être redirigé sur le site de paiement
              </Typography>

              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  window.open(paymentUrl, '_self')
                }}
              >
                Continuer
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Modal>
      {(form.data.funding_organism?.code === 'ALMA' ||
        (form.data.personal_funding !== null && Boolean(form.data.personal_funding)) ||
        (form.data.personal_funding === null && Boolean(form.data.default_personal_funding))) && (
        <div
          id="alma-in-page"
          style={{
            width: 0,
            height: 0,
            display: 'none',
          }}
        />
      )}

      <Formtitle
        title="Récapitulatif"
        subtitle="Retrouvez ci-dessous le récapitulatif de votre inscription, vérifiez bien les informations indiquées avant de continuer"
      />

      <Grid item xs={12}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6}>
            <Typography variant="body2">
              <i>Tous les prix indiqués ci-dessous sont TTC</i>
            </Typography>
          </Grid>
          <Grid item container xs={6} justifyContent="end">
            <Button variant="contained" size="medium" onClick={refetchRegistration}>
              Rafraîchir
            </Button>
          </Grid>
        </Grid>

        <Grid container direction="column" spacing={4} mt={3}>
          {form.data.validated && (
            <Alert sx={{ fontWeight: 500 }}>
              Votre inscription a déjà été validée, vous allez recevoir rapidement vos accès personnels à la plateforme
              de formation, directement sur l'adresse email indiquée ci-dessous.
            </Alert>
          )}
        </Grid>

        <Grid container direction="column" spacing={4} mt={1}>
          <Grid item container>
            <Typography variant="h5" fontWeight="500" mb={3}>
              {form.data.funding_organism?.type === 'opco'
                ? 'Informations personnelles du stagiaire'
                : 'Vos informations personnelles'}
            </Typography>

            <Grid item container direction="row" justifyContent="space-between">
              <Typography variant="body2">NOM, Prénom</Typography>
              <Typography variant="body1" sx={{ textAlign: 'right', fontWeight: 500 }}>
                {form.data.last_name?.toUpperCase() || '-'}, {form.data.first_name || '-'}
              </Typography>
            </Grid>
            <Grid item container direction="row" justifyContent="space-between">
              <Typography variant="body2">Email</Typography>
              <div>
                <Typography variant="body1" sx={{ textAlign: 'right', fontWeight: 500 }}>
                  {form.data.email}
                </Typography>
                <Typography variant="body2" sx={{ textAlign: 'right', fontWeight: 300, fontSize: '11px' }}>
                  L'accès à la plateforme d'apprentissage sera envoyé à cette adresse
                </Typography>
              </div>
            </Grid>
            {form.data.dpc_rpps && (
              <Grid item container direction="row" justifyContent="space-between">
                <Typography variant="body2">N° RPPS</Typography>
                <Typography variant="body1" sx={{ textAlign: 'right', fontWeight: 500 }}>
                  {form.data.dpc_rpps}
                </Typography>
              </Grid>
            )}
          </Grid>

          {session && (
            <Grid item container direction="column">
              <Typography variant="h5" fontWeight="500" mb={3}>
                Votre session de formation
              </Typography>
              <Grid item>
                <Grid container justifyContent="space-between" sx={{ mb: 1 }}>
                  <Grid item>
                    <Typography variant="body2">Début le</Typography>
                  </Grid>
                  <Grid item>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                      }}
                    >
                      {session
                        ? form.sessions.indexOf(session) === 0 && new Date(session.starting_date) <= new Date()
                          ? "Dès aujourd'hui"
                          : new Date(session.starting_date).toLocaleDateString('fr', {
                              day: '2-digit',
                              month: 'long',
                              year: 'numeric',
                            })
                        : '-'}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container justifyContent="space-between">
                  <Grid item>
                    <Typography variant="body2">À terminer avant le</Typography>
                  </Grid>
                  <Grid item>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                      }}
                    >
                      {session
                        ? session.display_ending_date
                          ? new Date(session.display_ending_date).toLocaleDateString('fr', {
                              day: '2-digit',
                              month: 'long',
                              year: 'numeric',
                            })
                          : new Date(session.ending_date).toLocaleDateString('fr', {
                              day: '2-digit',
                              month: 'long',
                              year: 'numeric',
                            })
                        : '-'}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <MetropolitanCvNote form={form} />
            </Grid>
          )}
          {!session && showMetropolitanCvNote && (
            <Grid item>
              <MetropolitanCvNote form={form} />
            </Grid>
          )}

          {!['STRIPE', 'GOCARDLESS'].includes(form.data.funding_organism?.code ?? '') && (
            <Grid item container direction="column">
              <Typography variant="h5" fontWeight="500" mb={3}>
                Financement
              </Typography>
              <FundingSummary form={form} />
            </Grid>
          )}
        </Grid>
        {['FIFPL', 'FAFPM'].includes(form.data.funding_organism?.code ?? '') && (
          <Grid item>
            <Rac form={form} setForm={setForm} />
          </Grid>
        )}
        {form.data.funding_organism?.code === 'DPC' && (
          <Grid item>
            <DPCContract form={form} setForm={setForm} reload={reload} />
          </Grid>
        )}
        {(form.data.funding_organism?.code === 'ALMA' || form.data.funding_organism?.code === 'BANK_TRANSFER') && (
          <Grid item>
            <AlmaContract form={form} setForm={setForm} reload={reload} />
          </Grid>
        )}

        {form.data.funding_organism?.code === 'STRIPE' && (
          <>
            {form.data.products.length > 0 && (
              <Grid sx={{ mt: 6 }}>
                <ProductSummary products={form.data.products} />
              </Grid>
            )}
            <Grid sx={{ mt: 6 }}>
              <StripeContract form={form} setForm={setForm} reload={reload} />
            </Grid>
          </>
        )}

        {form.data.funding_organism?.code === 'GOCARDLESS' && (
          <Grid item sx={{ mt: 4 }}>
            <Typography variant="h5" fontWeight="500" mb={3}>
              Votre échéancier de paiement
            </Typography>
            {gcError && (
              <Alert severity="error" onClose={() => setGcError(null)} sx={{ mb: 2 }}>
                {gcError}
              </Alert>
            )}
            {gcStatusLoading ? (
              <Grid container justifyContent="center">
                <CircularProgress />
              </Grid>
            ) : (
              <>
                {gcStatus?.card_registered && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Votre carte bancaire est déjà enregistrée.
                  </Alert>
                )}
                {gcScheduleSummary.sorted.length > 0 ? (
                  <Box
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                    }}
                  >
                    <Stack spacing={1}>
                      {gcScheduleSummary.cardPayment && (
                        <Typography variant="body1">
                          1<sup>er</sup> versement de {gcFormatter.format(gcScheduleSummary.cardPayment.amount)} par
                          carte bancaire le {formatGcDate(gcScheduleSummary.cardPayment.due_date)}
                        </Typography>
                      )}
                      {gcScheduleSummary.sepaPayments.length > 0 && (
                        <Typography variant="body1">
                          {gcScheduleSummary.cardPayment ? 'puis ' : ''}
                          {gcScheduleSummary.sepaPayments.length} prélèvement
                          {gcScheduleSummary.sepaPayments.length > 1 ? 's' : ''} SEPA
                          {gcScheduleSummary.lastSepaDate
                            ? ` jusqu'au ${gcScheduleSummary.lastSepaDate}`
                            : ''}
                        </Typography>
                      )}
                      <Typography variant="body1" fontWeight={600} sx={{ pt: 0.5 }}>
                        Total du solde : {gcFormatter.format(gcScheduleSummary.total)}
                      </Typography>
                    </Stack>
                    <Button
                      size="small"
                      sx={{ mt: 1.5, px: 0 }}
                      endIcon={gcScheduleExpanded ? <MdExpandLess size={20} /> : <MdExpandMore size={20} />}
                      onClick={() => setGcScheduleExpanded((expanded) => !expanded)}
                    >
                      {gcScheduleExpanded ? 'Masquer le détail' : 'Voir le détail'}
                    </Button>
                    <Collapse in={gcScheduleExpanded} timeout="auto" unmountOnExit>
                      <TableContainer sx={{ mt: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Date</TableCell>
                              <TableCell>Moyen</TableCell>
                              <TableCell align="right">Montant</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {gcScheduleSummary.sorted.map((inst, index) => (
                              <TableRow key={index}>
                                <TableCell>{formatGcDate(inst.due_date)}</TableCell>
                                <TableCell>
                                  {inst.provider === 'stripe' ? 'Carte bancaire' : 'Prélèvement SEPA'}
                                </TableCell>
                                <TableCell align="right">{gcFormatter.format(inst.amount)}</TableCell>
                              </TableRow>
                            ))}
                            <TableRow>
                              <TableCell />
                              <TableCell>
                                <Typography variant="body2" fontWeight="500">
                                  Total
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight="500">
                                  {gcFormatter.format(gcScheduleSummary.total)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Collapse>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    L&apos;échéancier n&apos;est pas encore disponible.
                  </Typography>
                )}
              </>
            )}
          </Grid>
        )}

        {shouldPay && (
          <Grid item my={5}>
            <InvoiceForm form={form} setForm={setForm} setBillingInfoComplete={setBillingInfoComplete} />
          </Grid>
        )}
        <Grid item sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          {form.data.funding_organism?.type !== 'opco' ? (
            <Button
              variant="outlined"
              disabled={location.pathname.includes('/information')}
              sx={{
                height: {
                  xs: 45,
                },
              }}
              startIcon={<MdNavigateBefore />}
              onClick={() => {
                navigate(-1)
              }}
            >
              Retour
            </Button>
          ) : (
            <div></div>
          )}

          <Button variant="contained" onClick={submitForm} disabled={isSubmitDisabled()}>
            {loading || gcActionLoading ? (
              <CircularProgress color="inherit" size={20} />
            ) : form.data.funding_organism?.code === 'GOCARDLESS' &&
              !form.data.validated &&
              !gcStatus?.card_registered ? (
              'Enregistrer ma carte'
            ) : !form.data.validated ? (
              shouldPay ? (
                'Payer'
              ) : (
                'Valider'
              )
            ) : (
              'Continuer'
            )}
          </Button>
        </Grid>
      </Grid>
      <StripeModal
        stripeSession={stripeSession}
        open={openStripeModal}
        setOpen={setOpenStripeModal}
        redirectUrl={stripeRedirectUrl}
      />
      <BankTransferModal form={form} open={openBankTransferModal} setOpen={setOpenBankTransferModal} />
    </Layout>
  )
}

export default Recap
